
import { useCallback, useState } from 'react';
import { Attendee } from '@/types/attendee';
import { AdminAction } from '@/types/adminTypes';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase/client';

type ToastFunction = {
  toast: (props: { title: string; description: string; variant?: "default" | "destructive" }) => void;
};

export const useAttendanceConfirmation = (
  recordAction: (actionType: AdminAction['actionType'], adminName: string, details?: string, attendeeCode?: string) => void,
  adminName: string,
  { toast }: ToastFunction
) => {
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  
  // Function to handle when an attendee is found
  const handleAttendeeFound = useCallback((attendee: Attendee) => {
    setSelectedAttendee(attendee);
    setIsDialogOpen(true);
  }, []);
  
  // Function to directly confirm attendance (for quicker flow)
  const directConfirmAttendance = useCallback(async (attendee: Attendee): Promise<boolean> => {
    if (!attendee) return false;
    
    try {
      const timestamp = new Date().toISOString();
      
      // Update attendee record
      const { error, data } = await supabase
        .from('attendees')
        .update({
          attended: true,
          checked_in_at: timestamp
        })
        .eq('code', attendee.code)
        .select();
      
      if (error) {
        console.error('Error during direct check-in:', error);
        return false;
      }
      
      // Log action
      recordAction(
        "checkin",
        adminName,
        `Direct check-in: ${attendee.name}`,
        attendee.code
      );
      
      // Create a copy of the attendee that's checked in
      const updatedAttendee = {
        ...attendee,
        attended: true,
        checkedInAt: timestamp
      };
      
      // Dispatch attendee-checked-in event to trigger t-shirt size modal
      // WITH useModalOnly flag to ensure we only show the modal
      const event = new CustomEvent('attendee-checked-in', {
        detail: { 
          attendee: updatedAttendee,
          useModalOnly: true
        }
      });
      window.dispatchEvent(event);
      
      // Trigger attendance-direct-confirmed event
      const confirmedEvent = new CustomEvent('attendance-direct-confirmed', {
        detail: { 
          attendeeCode: attendee.code,
          timestamp,
          useModalOnly: true
        }
      });
      window.dispatchEvent(confirmedEvent);
      
      return true;
    } catch (error) {
      console.error('Error during direct confirmation:', error);
      return false;
    }
  }, [adminName, recordAction]);

  // Enhance the confirm attendance function to trigger the t-shirt size modal
  const confirmAttendance = useCallback(async (): Promise<boolean> => {
    if (!selectedAttendee) return false;
    
    try {
      setIsConfirming(true);
      
      // Get current timestamp
      const timestamp = new Date().toISOString();
      
      // Update attendee record
      const { error, data } = await supabase
        .from('attendees')
        .update({
          attended: true,
          checked_in_at: timestamp
        })
        .eq('code', selectedAttendee.code)
        .select();
      
      if (error) {
        console.error('Error during confirmation:', error);
        
        toast({
          title: "Check-in failed",
          description: "An error occurred during check-in. Please try again.",
          variant: "destructive"
        });
        
        setIsConfirming(false);
        return false;
      }
      
      // Log action
      recordAction(
        "checkin",
        adminName,
        `${selectedAttendee.name} checked in`,
        selectedAttendee.code
      );
      
      // Create a copy of the attendee that's been updated
      const updatedAttendee = {
        ...selectedAttendee,
        attended: true,
        checkedInAt: timestamp
      };
      
      // Dispatch attendee-checked-in event to show t-shirt size modal
      // WITH useModalOnly flag to ensure we only show the modal
      const checkedInEvent = new CustomEvent('attendee-checked-in', {
        detail: { 
          attendee: updatedAttendee,
          useModalOnly: true
        }
      });
      window.dispatchEvent(checkedInEvent);
      
      // Close dialog after short delay
      setTimeout(() => {
        setIsDialogOpen(false);
        setSelectedAttendee(null);
        setIsConfirming(false);
        
        // Dispatch attendance confirmed event
        const confirmedEvent = new CustomEvent('attendance-confirmed', {
          detail: { 
            attendeeCode: selectedAttendee.code,
            timestamp,
            useModalOnly: true
          }
        });
        window.dispatchEvent(confirmedEvent);
      }, 500);
      
      return true;
    } catch (error) {
      console.error('Error during confirmation:', error);
      
      toast({
        title: "Check-in failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      
      setIsConfirming(false);
      return false;
    }
  }, [selectedAttendee, adminName, recordAction, toast]);

  return {
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    isConfirming,
    handleAttendeeFound,
    confirmAttendance,
    directConfirmAttendance,
    setSelectedAttendee
  };
};
